import BackButton from 'Components/BackButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import StyledForm from 'Components/StyledForm'
import SchoolCalendarCalendar from 'Services/ControlDataManagement/Features/SchoolCalendar/Components/SchoolCalendarCalendar'
import { SELECTED_SCHOOL_CALENDAR_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/SchoolCalendar/Hooks/useSchoolCalendarsWithParams'
import {
  useCreateSingleSchoolCalendarMutation,
  useDeleteSingleSchoolCalendarMutation,
  useUpdateSingleSchoolCalendarMutation
} from 'Services/ControlDataManagement/Features/SchoolCalendar/schoolCalendarApi'
import type { SchoolCalendar } from 'Services/ControlDataManagement/Features/SchoolCalendar/types'
import type { ControlDataServiceError, ControlDataServiceViolation } from 'Util/ApiClient'
import { formatViolationErrorField } from 'Util/formatters'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { Box, Grid } from '@mui/system'
import { addMonths, startOfYear } from 'date-fns'
import { memo, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  name: [],
  calendar_id: [],
  year: []
}

interface SchoolCalendarFormProps {
  successBaseUrl: string
  schoolCalendar?: SchoolCalendar
}

export default function SchoolCalendarForm({
  successBaseUrl,
  schoolCalendar
}: Readonly<SchoolCalendarFormProps>) {
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const isEdit = !!schoolCalendar

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i)
  const [schoolCalendarDetails, setSchoolCalendarDetails] = useState<Partial<SchoolCalendar>>(
    (isEdit && schoolCalendar) || {}
  )
  const [selectedYear, setSelectedYear] = useState<number>(
    Number.parseInt(schoolCalendarDetails?.year ?? '', 10) || new Date().getFullYear()
  )
  const [months, setMonths] = useState<Date[]>([])
  const [selectedDates, setSelectedDates] = useState<Record<number, Date[]>>({})

  const [createSingleSchoolCalendar, createStatus] = useCreateSingleSchoolCalendarMutation()
  const [updateSingleSchoolCalendar, updateStatus] = useUpdateSingleSchoolCalendarMutation()
  const [deleteSingleSchoolCalendar, { isLoading: isDeleteLoading }] =
    useDeleteSingleSchoolCalendarMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  // Helper function to check if a date is already selected
  const isDateSelected = useCallback(
    (date: Date, monthIndex: number) => {
      const currentMonthSelections = selectedDates[monthIndex] ?? []
      return currentMonthSelections.some(
        selectedDate =>
          selectedDate.getDate() === date.getDate() &&
          selectedDate.getMonth() === date.getMonth() &&
          selectedDate.getFullYear() === date.getFullYear()
      )
    },
    [selectedDates]
  )

  const handleDateSelect = useCallback((date: Date | null, monthIndex: number) => {
    if (!date) return

    setSelectedDates(prev => {
      const currentMonthSelections = prev[monthIndex] ?? []

      // Check if date is already selected
      const dateExists = currentMonthSelections.some(
        selectedDate =>
          selectedDate.getDate() === date.getDate() &&
          selectedDate.getMonth() === date.getMonth() &&
          selectedDate.getFullYear() === date.getFullYear()
      )

      if (dateExists) {
        // Remove date if already selected
        return {
          ...prev,
          [monthIndex]: currentMonthSelections.filter(
            selectedDate =>
              selectedDate.getDate() !== date.getDate() ||
              selectedDate.getMonth() !== date.getMonth() ||
              selectedDate.getFullYear() !== date.getFullYear()
          )
        }
      } else {
        // Add date if not selected
        return {
          ...prev,
          [monthIndex]: [...currentMonthSelections, date]
        }
      }
    })
  }, [])

  /**
   * Convert selected dates to a binary string representation. Calendar's days field is a string of 365 or 366 characters,
   * where each character represents a day of the year. '1' means the date is selected, '0' means it is not.
   * @param year The year for which to convert the dates.
   * @param selectedDates The selected dates to convert.
   * @returns A binary string representing the selected dates.
   */
  const convertSelectedDatesToBinaryString = (
    selectedDates: Record<number, Date[]>,
    year: number
  ) => {
    // Create an array of 365 or 366 zeroes (depending on leap year)
    const daysInYear = new Date(year, 1, 29).getDate() === 29 ? 366 : 365
    const binaryArray = new Array(daysInYear).fill('0')

    // Iterate through all selected dates
    Object.values(selectedDates).forEach(dates => {
      dates.forEach(date => {
        // Only process dates from the specified year
        if (date.getFullYear() === year) {
          // Calculate day of year using Date constructor to avoid DST issues
          const startOfYear = new Date(year, 0, 1)
          const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

          // Calculate difference in days using UTC to avoid timezone issues
          const dayOfYear = Math.floor(
            (Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()) -
              Date.UTC(startOfYear.getFullYear(), startOfYear.getMonth(), startOfYear.getDate())) /
              (24 * 60 * 60 * 1000)
          )

          // Set the corresponding position to '1'
          if (dayOfYear >= 0 && dayOfYear < daysInYear) {
            binaryArray[dayOfYear] = '1'
          }
        }
      })
    })

    return binaryArray.join('')
  }

  /**
   * Convert a binary string representation of selected dates back to a record of dates.
   * @param binaryString
   * @param year
   * @returns
   */
  const convertBinaryStringToSelectedDates = (
    binaryString: string,
    year: number
  ): Record<number, Date[]> => {
    const selectedDates: Record<number, Date[]> = {}
    const daysInYear = binaryString.length
    const startOfYearDate = new Date(year, 0, 1)

    for (let i = 0; i < daysInYear; i++) {
      if (binaryString[i] === '1') {
        // Create date by adding i days to start of year
        const date = new Date(startOfYearDate)
        date.setDate(startOfYearDate.getDate() + i)

        const monthIndex = date.getMonth()
        if (!selectedDates[monthIndex]) {
          selectedDates[monthIndex] = []
        }
        selectedDates[monthIndex].push(date)
      }
    }

    return selectedDates
  }

  const handleChange = (value: string | undefined, field: keyof SchoolCalendar) => {
    if (value !== undefined) {
      setSchoolCalendarDetails(prevState => ({ ...prevState, [field]: value }) as SchoolCalendar)
    }
  }

  const handleYearChange = (value: string | number | undefined) => {
    if (value !== undefined) {
      setSchoolCalendarDetails(
        prevState =>
          ({
            ...prevState,
            year: value
          }) as SchoolCalendar
      )
      setSelectedYear(value as number)
      setSelectedDates({})
    }
  }

  /**
   * Get all months for a given year
   * @param year The year for which to get the months
   * @returns An array of Date objects representing each month in the year
   */
  const getMonthsForYear = (year: number) => {
    const startDate = startOfYear(new Date(year, 0, 1))
    return Array.from({ length: 12 }, (_, i) => addMonths(startDate, i))
  }

  useEffect(() => {
    const newMonths = getMonthsForYear(selectedYear)
    setMonths(newMonths)
  }, [selectedYear])

  useEffect(() => {
    if (schoolCalendar) {
      setSelectedDates(convertBinaryStringToSelectedDates(schoolCalendar.days, selectedYear))
    }
  }, [schoolCalendar])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const daysString = convertSelectedDatesToBinaryString(selectedDates, selectedYear)

    const calendarData = {
      ...schoolCalendarDetails,
      year: selectedYear.toString(),
      days: daysString
    }

    const mutation = isEdit
      ? updateSingleSchoolCalendar({
          id: schoolCalendar.id,
          ...calendarData
        })
      : createSingleSchoolCalendar(calendarData)

    mutation
      .unwrap()
      .then((calendarResponse: SchoolCalendar) => {
        SnackbarUtils.success(t`Calendar successfully saved.`)
        if (isEdit) {
          navigate(successBaseUrl)
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_SCHOOL_CALENDAR_PARAMETER_KEY]: calendarResponse.id
            })
          )
        }
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { violations, message } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error saving the calendar.`)
        }
      })
  }

  const handleDelete = () => {
    if (!schoolCalendar) return

    deleteSingleSchoolCalendar(schoolCalendar.id)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Calendar successfully deleted.`)
        navigate(successBaseUrl)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the calendar.`)
      })
  }

  function setValidationErrors(violations: ControlDataServiceViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading || isDeleteLoading}>
          <Box display="flex" justifyContent="space-between">
            <BackButton label={t`Back`} link={successBaseUrl} />
            <Box>
              {isEdit ? (
                <>
                  <DeleteButton
                    size="small"
                    variant="text"
                    onClick={() =>
                      confirm({
                        title: t`Are you sure?`,
                        message: t`Are you sure you want to delete the calendar?`,
                        handleConfirm: () => {
                          handleDelete()
                        }
                      })
                    }
                    sx={{ marginRight: 1 }}
                  >{t`Delete calendar`}</DeleteButton>
                  <CustomLoadingButton type="submit" isLoading={isSubmitLoading} iconPosition="end">
                    {t`Save changes`}
                  </CustomLoadingButton>
                </>
              ) : (
                <CustomLoadingButton type="submit" isLoading={isSubmitLoading} iconPosition="end">
                  {t`Save and create`}
                </CustomLoadingButton>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <TextField
                name="name"
                required
                label={t`Calendar name`}
                size="small"
                value={schoolCalendarDetails?.name ?? ''}
                onChange={e => handleChange(e.target.value, 'name')}
                sx={{ minWidth: 200 }}
                error={fieldErrors.name.length > 0}
                helperText={fieldErrors.name.length > 0 ? fieldErrors.name[0] : ''}
              />
            </Box>
            <Box>
              <TextField
                name="calendar_id"
                required
                label={t`Calendar identifier`}
                type="number"
                size="small"
                disabled={isEdit}
                value={schoolCalendarDetails?.calendar_id ?? ''}
                onChange={e => handleChange(e.target.value, 'calendar_id')}
                sx={{ minWidth: 200 }}
                error={fieldErrors.calendar_id.length > 0}
                helperText={fieldErrors.calendar_id.length > 0 ? fieldErrors.calendar_id[0] : ''}
              />
            </Box>
          </Box>
          <Box>
            <FormControl sx={{ minWidth: 200 }} required>
              <InputLabel size="small">{t`Year`}</InputLabel>
              <Select
                name="year"
                value={schoolCalendarDetails?.year ?? ''}
                label={t`Year`}
                onChange={e => handleYearChange(e.target.value)}
                size="small"
                error={fieldErrors.year.length > 0}
              >
                {yearOptions.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={fieldErrors.year.length > 0}>
                {fieldErrors.year.length > 0 ? fieldErrors.year[0] : ''}
              </FormHelperText>
            </FormControl>
          </Box>
          <Box>
            <Calendar
              months={months}
              selectedYear={selectedYear}
              handleDateSelect={handleDateSelect}
              isDateSelected={isDateSelected}
              setSelectedDates={setSelectedDates}
            />
          </Box>
        </StyledForm>
      </Grid>
    </Grid>
  )
}

const Calendar = memo(function Calendar({
  selectedYear,
  months,
  handleDateSelect,
  isDateSelected,
  setSelectedDates
}: Readonly<{
  selectedYear: number
  months: Date[]
  handleDateSelect: (date: Date | null, monthIndex: number) => void
  isDateSelected: (date: Date, monthIndex: number) => boolean
  setSelectedDates: React.Dispatch<React.SetStateAction<Record<number, Date[]>>>
}>) {
  return (
    <SchoolCalendarCalendar
      selectedYear={selectedYear}
      months={months}
      handleDateSelect={handleDateSelect}
      isDateSelected={isDateSelected}
      setSelectedDates={setSelectedDates}
    />
  )
})
